'use client'

import { Page, Card, Layout, Text, BlockStack, Link } from '@shopify/polaris'
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_LOCAL,
  SUPPORT_PHONE_TEL_HREF,
  SUPPORT_WHATSAPP_URL,
} from '@/lib/contact'

export default function PrivacyPage() {
  return (
    <Page title="Privacy Policy">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Privacy Policy</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Last updated: March 4, 2026
              </Text>
              <Text as="p" variant="bodyMd">
                Sigma Sense (&quot;the App&quot;) provides analytics, order management, and courier integrations (&quot;the Service&quot;)
                to merchants who use Shopify to power their stores. This Privacy Policy describes how personal
                information is collected, used, and shared when you install or use the App in connection with
                your Shopify-supported store.
              </Text>

              <Text as="h3" variant="headingMd">Personal Information the App Collects</Text>
              <Text as="p" variant="bodyMd">
                When you install the App, we are automatically able to access certain types of information from your
                Shopify account, such as:
              </Text>
              <Text as="p" variant="bodyMd">
                - Orders data (read)
                - Products and SKUs (read)
                - Customers (read)
                - Store information (read)
                - Locations (read)
              </Text>
              <Text as="p" variant="bodyMd">
                Additionally, we collect the following types of personal information from you and/or your customers
                once you have installed the App: information about you and others who may access the App on behalf
                of your store, such as name, address, email address, phone number, and order data.
              </Text>
              <Text as="p" variant="bodyMd">
                We collect personal information directly from the relevant individual, through your Shopify account,
                or using the following technologies: &quot;Cookies&quot; are data files that are placed on your device or
                computer and often include an anonymous unique identifier. For more information about cookies, and
                how to disable cookies, visit http://www.allaboutcookies.org. &quot;Log files&quot; track actions occurring on
                the Site, and collect data including your IP address, browser type, Internet service provider,
                referring/exit pages, and date/time stamps. &quot;Web beacons,&quot; &quot;tags,&quot; and &quot;pixels&quot; are electronic files
                used to record information about how you browse the Site (for example, analytics tools).
              </Text>

              <Text as="h3" variant="headingMd">How Do We Use Your Personal Information?</Text>
              <Text as="p" variant="bodyMd">
                We use the personal information we collect from you and your customers in order to provide the
                Service and to operate the App. Additionally, we use this personal information to: communicate with
                you; optimize or improve the App; and provide you with information relating to our products or
                services.
              </Text>

              <Text as="h3" variant="headingMd">Sharing Your Personal Information</Text>
              <Text as="p" variant="bodyMd">
                We may share your Personal Information to comply with applicable laws and regulations, to respond to
                a subpoena, search warrant, or other lawful request for information we receive, or to otherwise
                protect our rights. We may also share data with courier services and service providers strictly to
                deliver the features you enable.
              </Text>

              <Text as="h3" variant="headingMd">Marketing</Text>
              <Text as="p" variant="bodyMd">
                We do not use your Personal Information for behavioural advertising or targeted ads. For more information about how targeted
                advertising works, you can visit the Network Advertising Initiative&apos;s (&quot;NAI&quot;) educational page at
                http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work.
              </Text>
              <Text as="p" variant="bodyMd">
                You can opt out of targeted advertising by visiting the Digital Advertising Alliance&apos;s opt-out portal:
                http://optout.aboutads.info/.
              </Text>

              <Text as="h3" variant="headingMd">Your Rights</Text>
              <Text as="p" variant="bodyMd">
                If you are a European resident, you have the right to access personal information we hold about you
                and to ask that your personal information be corrected, updated, or deleted. If you would like to
                exercise this right, please contact us through the contact information below.
              </Text>
              <Text as="p" variant="bodyMd">
                Additionally, if you are a European resident we note that we are processing your information in order
                to fulfill contracts we might have with you (for example if you make an order through the Site), or
                otherwise to pursue our legitimate business interests listed above. Additionally, please note that
                your information will be transferred outside of Europe, including to Canada and the United States.
              </Text>

              <Text as="h3" variant="headingMd">Data Retention</Text>
              <Text as="p" variant="bodyMd">
                When you place an order through the Site, we will maintain your Order Information for our records
                unless and until you ask us to delete this information.
              </Text>

              <Text as="h3" variant="headingMd">Changes</Text>
              <Text as="p" variant="bodyMd">
                We may update this privacy policy from time to time in order to reflect, for example, changes to our
                practices or for other operational, legal, or regulatory reasons.
              </Text>

              <Text as="h3" variant="headingMd">Advertising Platform Integrations</Text>
              <Text as="p" variant="bodyMd">
                Sigma Sense integrates with Meta (Facebook &amp; Instagram Ads) to provide ad performance analytics.
                We only <strong>read</strong> ad performance data via the Meta Insights API—including campaign_id,
                campaign_name, spend, and currency. We do <strong>not</strong> create, edit, or manage ads.
              </Text>
              <Text as="p" variant="bodyMd">
                OAuth tokens are stored encrypted. You can disconnect your Meta account at any time from the App
                settings.
              </Text>
              <Text as="p" variant="bodyMd">
                <strong>Data retention:</strong> Daily spend records are kept for analytics. You may request deletion
                of your data at any time. For deletion requests or questions, contact us at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                , phone{' '}
                <a href={SUPPORT_PHONE_TEL_HREF}>{SUPPORT_PHONE_DISPLAY}</a>
                {' '}(within Pakistan: {SUPPORT_PHONE_LOCAL}),{' '}
                <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
                , or use our{' '}
                <Link url="/contact">contact form</Link>.
              </Text>

              <Text as="h3" variant="headingMd">Contact Us</Text>
              <Text as="p" variant="bodyMd">
                For more information about our privacy practices, if you have questions, or if you would like to make
                a complaint, please contact us by e-mail at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                , phone{' '}
                <a href={SUPPORT_PHONE_TEL_HREF}>{SUPPORT_PHONE_DISPLAY}</a>
                {' '}(within Pakistan: {SUPPORT_PHONE_LOCAL}), or{' '}
                <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
                .
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}
